import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Switch,
  Table,
  notification,
  Select,
  DatePicker,
  TimePicker,
  Row,
  Col,
  Alert,
  Collapse,
} from "antd";
import api from "../../api/axiosConfig";
import dayjs from "dayjs";
import NavBar from "../Layout/NavBar";

const { Option } = Select;
const { Panel } = Collapse;

const RehabDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("rehabToken"));
  const [rehabCenterDetails, setRehabCenterDetails] = useState({ name: "" });
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState("");
  const [workingHours, setWorkingHours] = useState({
    openFrom: "",
    openTo: "",
    isOpen24Hours: false,
  });
  const [appointments, setAppointments] = useState([]);
  const [addAppointmentForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loginForm] = Form.useForm();
  const [forgotPasswordForm] = Form.useForm();
  const [bookingSearchTerm, setBookingSearchTerm] = useState("");
  const [bookingDateFilter, setBookingDateFilter] = useState(null);

  const [alerts, setAlerts] = useState({});
  const [showRegistrationAlert, setShowRegistrationAlert] = useState(false);
  const [view, setView] = useState('login'); // login, forgotPassword, resetPassword
  const [forgotPasswordPhoneNumber, setForgotPasswordPhoneNumber] = useState('');
  const [loginError, setLoginError] = useState('');
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.fromRegistration) {
      setShowRegistrationAlert(true);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const handleLogout = () => {
    localStorage.removeItem("rehabToken");
    setIsLoggedIn(false);
    message.success("You have been logged out.");
    navigate("/rehab-dashboard");
  };

  const fetchRehabCenterDetails = async () => {
    try {
      const response = await api.get("/rehab-auth/details");
      setRehabCenterDetails(response.data);
    } catch (error) {
      message.error("Failed to fetch rehab center details.");
    }
  };

  const onLoginFinish = async (values) => {
    setLoginError('');
    try {
      const response = await api.post("/rehab-auth/login", values);
      localStorage.setItem("rehabToken", response.data.token);
      setIsLoggedIn(true);
      message.success("Login successful!");
    } catch (error) {
      setLoginError("Invalid credentials");
    }
  };
  
  const handleSendForgotPasswordOtp = async (values) => {
    try {
        const response = await api.post('/rehab-auth/forgot-password-otp', { phone_number: values.phone_number });
        setForgotPasswordPhoneNumber(values.phone_number);
        message.success(response.data.message);
        setView('resetPassword');
    } catch (error) {
        message.error(error.response?.data?.message || 'An error occurred.');
    }
  };

  const handleResetPasswordWithOtp = async (values) => {
      try {
          const payload = { ...values, phone_number: forgotPasswordPhoneNumber };
          await api.post('/rehab-auth/reset-password-with-otp', payload);
          setPasswordResetSuccess(true);
          setView('login');
          forgotPasswordForm.resetFields();
      } catch (error) {
          message.error(error.response?.data?.message || 'Failed to reset password.');
      }
  };

  const fetchServices = async () => {
    try {
      const response = await api.get("/rehab-auth/services");
      setServices(response.data.services);
    } catch (error) {
      message.error("Failed to fetch services.");
    }
  };

  const fetchWorkingHours = async () => {
    try {
      const response = await api.get("/rehab-auth/working-hours");
      setWorkingHours(response.data);
    } catch (error) {
      message.error("Failed to fetch working hours.");
    }
  };

  const handleUpdateWorkingHours = async () => {
    try {
      await api.put("/rehab-auth/working-hours", workingHours);
      setAlerts({ ...alerts, manageWorkingHours: { type: 'success', message: 'Working hours updated successfully!' } });
    } catch (error) {
      setAlerts({ ...alerts, manageWorkingHours: { type: 'error', message: 'Failed to update working hours.' } });
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await api.get("/rehab-auth/appointments");
      setAppointments(response.data);
    } catch (error) {
      message.error("Failed to fetch appointments.");
    }
  };

  const handleAddAppointment = async (values) => {
    try {
      const formattedValues = {
        ...values,
        date: values.date.format('YYYY-MM-DD'),
        time: values.time.format('HH:mm:ss'),
      };
      await api.post("/rehab-auth/appointments", formattedValues);
      setAlerts({ ...alerts, addAppointment: { type: 'success', message: 'Appointment added successfully!' } });
      addAppointmentForm.resetFields();
      fetchAppointments();
    } catch (error) {
      setAlerts({ ...alerts, addAppointment: { type: 'error', message: error.response?.data?.message || "Failed to add appointment." } });
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchRehabCenterDetails();
      fetchServices();
      fetchWorkingHours();
      fetchAppointments();
    }
  }, [isLoggedIn]);

  const handleAddService = async () => {
    try {
      await api.post("/rehab-auth/services", { service: newService });
      setNewService("");
      fetchServices();
      setAlerts({ ...alerts, manageServices: { type: 'success', message: 'Service added successfully!' } });
    } catch (error) {
      setAlerts({ ...alerts, manageServices: { type: 'error', message: 'Failed to add service.' } });
    }
  };

  const handleDeleteService = async (service) => {
    try {
      await api.delete("/rehab-auth/services", { data: { service } });
      fetchServices();
      setAlerts({ ...alerts, manageServices: { type: 'success', message: 'Service deleted successfully!' } });
    } catch (error) {
      setAlerts({ ...alerts, manageServices: { type: 'error', message: 'Failed to delete service.' } });
    }
  };
  
  const handleChangePassword = async (values) => {
    try {
      await api.post("/rehab-auth/change-password", values);
      setAlerts({ ...alerts, changePassword: { type: 'success', message: 'Password changed successfully!' } });
      passwordForm.resetFields();
    } catch (error) {
      setAlerts({ ...alerts, changePassword: { type: 'error', message: error.response?.data?.message || "Failed to change password." } });
    }
  };

  const disabledTime = () => {
    if (!selectedDate) {
      return { disabledHours: () => [...Array(24).keys()] };
    }

    const now = dayjs();
    const isToday = selectedDate.isSame(now, 'day');

    const bookedTimes = appointments
      .filter(a => dayjs(a.booking_date).isSame(selectedDate, 'day'))
      .map(a => dayjs(a.booking_time, 'HH:mm:ss'));

    let openTime = dayjs().startOf('day');
    let closeTime = dayjs().endOf('day');

    if (!workingHours.isOpen24Hours && workingHours.openFrom && workingHours.openTo) {
        openTime = dayjs(workingHours.openFrom, 'HH:mm');
        closeTime = dayjs(workingHours.openTo, 'HH:mm');
    }

    const disabledHours = () => {
      const hours = [];
      // Disable hours outside working hours
      for (let i = 0; i < openTime.hour(); i++) hours.push(i);
      for (let i = closeTime.hour() + 1; i < 24; i++) hours.push(i);
      
      // Disable past hours if today
      if (isToday) {
        for (let i = 0; i < now.hour(); i++) hours.push(i);
      }
      return [...new Set(hours)];
    };

    const disabledMinutes = (selectedHour) => {
      let minutes = [];

      // Handle times outside of working hours
      if (selectedHour < openTime.hour() || selectedHour > closeTime.hour()) return [...Array(60).keys()];
      if (selectedHour === openTime.hour()) {
        for (let i = 0; i < openTime.minute(); i++) minutes.push(i);
      }
      if (selectedHour === closeTime.hour()) {
        // For close time, we disable from that minute onwards
        for (let i = closeTime.minute(); i < 60; i++) minutes.push(i);
      }

      // Handle booked times
      bookedTimes.forEach(bt => {
        if (bt.hour() === selectedHour) {
          minutes.push(bt.minute());
        }
      });

      // Handle past minutes if today
      if (isToday && selectedHour === now.hour()) {
        // disable minutes up to and including the current minute. 
        for (let i = 0; i <= now.minute(); i++) minutes.push(i); 
      }
      
      return [...new Set(minutes)];
    };

    return {
      disabledHours,
      disabledMinutes,
    };
  };

  const filteredAppointments = appointments.filter(appointment => {
    const searchTermMatch = !bookingSearchTerm ||
      (appointment.name && appointment.name.toLowerCase().includes(bookingSearchTerm.toLowerCase())) ||
      (appointment.phone_number && appointment.phone_number.includes(bookingSearchTerm));

    const dateMatch = !bookingDateFilter ||
      dayjs(appointment.booking_date).isSame(bookingDateFilter, 'day');

    return searchTermMatch && dateMatch;
  });

  if (!isLoggedIn) {
    return (
      <>
        <NavBar showNavLinks={false} showRegisterButton={false} showLogoutButton={false} />
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "calc(100vh - 80px)" }}>
          {showRegistrationAlert && (
            <Alert
              message="🎉 Registration Successful!"
              description="Your details have been submitted for verification. Please log in to access your dashboard."
              type="success"
              showIcon
              closable
              onClose={() => setShowRegistrationAlert(false)}
              style={{ marginBottom: '1rem', width: 400 }}
            />
          )}
          <Card title={view === 'login' ? "Rehab Center Login" : "Forgot Password"} style={{ width: 400 }}>
            {passwordResetSuccess && (
                <Alert
                    message="Password updated successfully!"
                    description="You can now log in with your new password."
                    type="success"
                    showIcon
                    closable
                    onClose={() => setPasswordResetSuccess(false)}
                    style={{ marginBottom: '1rem' }}
                />
            )}
            {loginError && (
                <Alert
                    message="Login Failed"
                    description={loginError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setLoginError('')}
                    style={{ marginBottom: '1rem' }}
                />
            )}
            {view === 'login' && (
              <Form form={loginForm} name="login" onFinish={onLoginFinish}>
                <Form.Item name="email" rules={[{ required: true, message: "Please input your email!" }]}>
                  <Input placeholder="Email" />
                </Form.Item>
                <Form.Item name="password" rules={[{ required: true, message: "Please input your password!" }]}>
                  <Input.Password placeholder="Password" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" style={{ width: "100%" }}>Log In</Button>
                </Form.Item>
                <Button type="link" onClick={() => setView('forgotPassword')}>Forgot Password? Click here</Button>
              </Form>
            )}
            {view === 'forgotPassword' && (
              <Form form={forgotPasswordForm} onFinish={handleSendForgotPasswordOtp}>
                <Form.Item name="phone_number" rules={[{ required: true, message: "Please enter your registered phone number!" }]}>
                  <Input placeholder="Enter your registered phone number" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" style={{ width: "100%" }}>Send OTP</Button>
                </Form.Item>
                <Button type="link" onClick={() => setView('login')}>Back to Login</Button>
              </Form>
            )}
            {view === 'resetPassword' && (
                <Form form={forgotPasswordForm} onFinish={handleResetPasswordWithOtp}>
                    <p>An OTP has been sent to your registered phone number {forgotPasswordPhoneNumber}.</p>
                    <Form.Item name="otp" rules={[{ required: true, message: "Please input the OTP!" }]}>
                        <Input placeholder="OTP" />
                    </Form.Item>
                    <Form.Item name="newPassword" rules={[{ required: true, message: "Please enter your new password!" }]}>
                        <Input.Password placeholder="New Password" />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" style={{ width: "100%" }}>Reset Password</Button>
                    </Form.Item>
                    <Button type="link" onClick={() => setView('login')}>Back to Login</Button>
                </Form>
            )}
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <NavBar showNavLinks={false} showRegisterButton={false} showLogoutButton={true} handleLogout={handleLogout} />
      <div style={{ padding: "2rem" }}>
        <h1>{rehabCenterDetails.name}</h1>

        <Collapse defaultActiveKey={['0']} style={{ marginTop: "2rem" }}>
          <Panel header="Add New Appointment" key="1">
            {alerts.addAppointment && (
              <Alert message={alerts.addAppointment.message} type={alerts.addAppointment.type} showIcon closable onClose={() => setAlerts({ ...alerts, addAppointment: null })} style={{ marginBottom: '1rem' }} />
            )}
            <Form form={addAppointmentForm} layout="vertical" onFinish={handleAddAppointment}>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="name" label="Patient Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="age" label="Patient Age" rules={[{ required: true }]}><Input type="number" /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="gender" label="Patient Gender" rules={[{ required: true }]}><Select><Option value="Male">Male</Option><Option value="Female">Female</Option><Option value="Other">Other</Option></Select></Form.Item></Col>
                <Col span={12}><Form.Item name="contact_number" label="Contact Number" rules={[{ required: true, len: 10 }]}><Input /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="email" label="Email" rules={[{ type: "email" }]}><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="service" label="Service" rules={[{ required: true }]}><Select>{services.map((s) => (<Option key={s} value={s}>{s}</Option>))}</Select></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="date" label="Booking Date" rules={[{ required: true }]}><DatePicker style={{ width: "100%" }} disabledDate={(current) => current && current < dayjs().startOf('day')} onChange={(date) => setSelectedDate(date)} /></Form.Item></Col>
                <Col span={12}><Form.Item name="time" label="Booking Time" rules={[{ required: true }]}><TimePicker use12Hours format="h:mm a" style={{ width: "100%" }} minuteStep={30} disabledTime={disabledTime} /></Form.Item></Col>
              </Row>
              <Form.Item><Button type="primary" htmlType="submit">Add Appointment</Button></Form.Item>
            </Form>
          </Panel>
        </Collapse>

        <Card title="Appointment History" style={{ marginTop: "2rem" }}>
          <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between" }}>
            <Input.Search
              placeholder="Search by patient name or contact number"
              value={bookingSearchTerm}
              onChange={(e) => setBookingSearchTerm(e.target.value)}
              style={{ width: 400 }}
            />
            <DatePicker
              onChange={(date) => setBookingDateFilter(date)}
              minuteStep={30}
              style={{ width: 200 }}
            />
          </div>
          <Table dataSource={filteredAppointments} columns={[{ title: "Name", dataIndex: "name" }, { title: "Phone Number", dataIndex: "phone_number" }, { title: "Booking Date", dataIndex: "booking_date", render: (t) => new Date(t).toLocaleDateString() }, { title: "Booking Time", dataIndex: "booking_time" }]} rowKey="id" />
        </Card>

        <Card title="Manage Services" style={{ marginTop: "2rem" }}>
          {alerts.manageServices && <Alert message={alerts.manageServices.message} type={alerts.manageServices.type} showIcon closable onClose={() => setAlerts({ ...alerts, manageServices: null })} style={{ marginBottom: '1rem' }} />}
          <Form onFinish={handleAddService}>
            <Form.Item><Input value={newService} onChange={(e) => setNewService(e.target.value)} placeholder="Enter a new service" /></Form.Item>
            <Form.Item><Button type="primary" htmlType="submit">Add Service</Button></Form.Item>
          </Form>
          <ul>
            {services.map((service) => (
              <li key={service}>{service} <Button type="link" danger onClick={() => handleDeleteService(service)}>Delete</Button></li>
            ))}
          </ul>
        </Card>

        <Card title="Manage Working Hours" style={{ marginTop: "2rem" }}>
          {alerts.manageWorkingHours && <Alert message={alerts.manageWorkingHours.message} type={alerts.manageWorkingHours.type} showIcon closable onClose={() => setAlerts({ ...alerts, manageWorkingHours: null })} style={{ marginBottom: '1rem' }} />}
          <Form onFinish={handleUpdateWorkingHours}>
            <Form.Item label="Open 24 Hours"><Switch checked={workingHours.isOpen24Hours} onChange={(c) => setWorkingHours({ ...workingHours, isOpen24Hours: c })} /></Form.Item>
            {!workingHours.isOpen24Hours && (
              <>
                <Form.Item label="Open From"><Input type="time" value={workingHours.openFrom || ""} onChange={(e) => setWorkingHours({ ...workingHours, openFrom: e.target.value })} /></Form.Item>
                <Form.Item label="Open To"><Input type="time" value={workingHours.openTo || ""} onChange={(e) => setWorkingHours({ ...workingHours, openTo: e.target.value })} /></Form.Item>
              </>
            )}
            <Form.Item><Button type="primary" htmlType="submit">Update Working Hours</Button></Form.Item>
          </Form>
        </Card>

      </div>
    </>
  );
};

export default RehabDashboard;